export type CrewStatisticsCatalogEntry = {
  name: string;
  path: string;
  description: string;
};

export type CrewStatisticsCatalogGroup = {
  name: string;
  description: string;
  items: CrewStatisticsCatalogEntry[];
};

/** Separate statistic views, grouped like the reports hub. */
export const CREW_STATISTICS_CATALOG: CrewStatisticsCatalogGroup[] = [
  {
    name: 'Operations',
    description: 'Onboard headcount, relief availability, and NR flags',
    items: [
      {
        name: 'Onboard',
        path: '/crew/statistics/onboard',
        description: 'Count of onboard crew per vessel, nationality, and months onboard',
      },
      {
        name: 'Available',
        path: '/crew/statistics/available',
        description: 'Count of available crew per last vessel, nationality, and months until available',
      },
      {
        name: 'Not recommended',
        path: '/crew/statistics/not-recommended',
        description: 'Count of not-recommended crew per last vessel, nationality, and appraisal average',
      },
      {
        name: 'Nationalities',
        path: '/crew/statistics/nationalities',
        description: 'Onboard nationality mix for a vessel, with safe manning flags',
      },
    ],
  },
  {
    name: 'Compliance',
    description: 'Certificates and medicals',
    items: [
      {
        name: 'Certs expired',
        path: '/crew/statistics/compliance/certs-expired',
        description: 'Count of expired certificates per rank, category, and days overdue',
      },
      {
        name: 'Certs expiring',
        path: '/crew/statistics/compliance/certs-expiring',
        description: 'Count of expiring certificates per rank, category, and days until expiry',
      },
      {
        name: 'Medicals expired',
        path: '/crew/statistics/compliance/medicals-expired',
        description: 'Count of expired medicals per rank, kind, and days overdue',
      },
      {
        name: 'Medicals due soon',
        path: '/crew/statistics/compliance/medicals-due',
        description: 'Count of medicals due soon per rank, kind, and days until due',
      },
    ],
  },
  {
    name: 'People & payroll',
    description: 'Appraisals, tenure, wages, and allottees',
    items: [
      {
        name: 'Appraisals / NR set',
        path: '/crew/statistics/people-payroll/appraisals',
        description: 'Crew with appraisals or NR flag set',
      },
      {
        name: 'NR count',
        path: '/crew/statistics/people-payroll/not-recommended',
        description: 'Crew flagged as not recommended',
      },
      {
        name: 'Avg months onboard',
        path: '/crew/statistics/people-payroll/avg-months-onboard',
        description: 'Average months on current ONB tour',
      },
      {
        name: 'Contract wage total',
        path: '/crew/statistics/people-payroll/contract-wages',
        description: 'Sum of contract wages for period assignments',
      },
      {
        name: 'Allottees',
        path: '/crew/statistics/people-payroll/allottees',
        description: 'Count of standing allottees',
      },
      {
        name: 'Allottee amount',
        path: '/crew/statistics/people-payroll/allottee-amount',
        description: 'Total standing allottee amounts',
      },
    ],
  },
  {
    name: 'Mix & charts',
    description: 'Nationality, rank, and expiry bucket breakdowns',
    items: [
      {
        name: 'Nationality mix',
        path: '/crew/statistics/mix/nationality',
        description: 'Onboard headcount by nationality',
      },
      {
        name: 'Rank mix',
        path: '/crew/statistics/mix/rank',
        description: 'Onboard headcount by rank',
      },
      {
        name: 'Certificate expiry buckets',
        path: '/crew/statistics/mix/cert-expiry',
        description: 'Certificate expiry timing buckets with detail list',
      },
    ],
  },
  {
    name: 'Age statistics',
    description: 'Average / min / max age for currently onboard crew',
    items: [
      {
        name: 'Age — per rank',
        path: '/crew/statistics/age/rank',
        description: 'Age distribution grouped by rank',
      },
      {
        name: 'Age — per rank category',
        path: '/crew/statistics/age/rank-category',
        description: 'Age distribution for Officers vs Ratings',
      },
      {
        name: 'Age — per vessel',
        path: '/crew/statistics/age/vessel',
        description: 'Age distribution grouped by vessel',
      },
    ],
  },
  {
    name: 'Sign-off statistics',
    description: 'Sign-offs in the selected period',
    items: [
      {
        name: 'Sign-off — per rank',
        path: '/crew/statistics/sign-off/rank',
        description: 'Sign-off counts grouped by rank',
      },
      {
        name: 'Sign-off — per rank category',
        path: '/crew/statistics/sign-off/rank-category',
        description: 'Sign-off counts for Officers vs Ratings',
      },
      {
        name: 'Sign-off — per vessel',
        path: '/crew/statistics/sign-off/vessel',
        description: 'Sign-off counts grouped by vessel',
      },
    ],
  },
  {
    name: 'Sign-on statistics',
    description: 'Sign-ons in the selected period',
    items: [
      {
        name: 'Sign-on — per rank',
        path: '/crew/statistics/sign-on/rank',
        description: 'Sign-on counts grouped by rank',
      },
      {
        name: 'Sign-on — per rank category',
        path: '/crew/statistics/sign-on/rank-category',
        description: 'Sign-on counts for Officers vs Ratings',
      },
      {
        name: 'Sign-on — per vessel',
        path: '/crew/statistics/sign-on/vessel',
        description: 'Sign-on counts grouped by vessel',
      },
    ],
  },
  {
    name: 'Appraisal grade',
    description: 'Average employment-status grade (sort order) from appraisals in period',
    items: [
      {
        name: 'Average appraisal grade — per rank',
        path: '/crew/statistics/appraisal-grade/rank',
        description: 'Average appraisal grade grouped by rank',
      },
      {
        name: 'Average appraisal grade — per rank category',
        path: '/crew/statistics/appraisal-grade/rank-category',
        description: 'Average appraisal grade for Officers vs Ratings',
      },
      {
        name: 'Average appraisal grade — per vessel',
        path: '/crew/statistics/appraisal-grade/vessel',
        description: 'Average appraisal grade grouped by vessel',
      },
    ],
  },
];
